import React, { useEffect, useState, useRef } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import InputBase from "@mui/material/InputBase";
import { alpha, styled } from "@mui/material/styles";
import { Box } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import PhoneIcon from "@mui/icons-material/Phone";
import PeopleIcon from "@mui/icons-material/People";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import Icon from "@mui/material/Icon";
import { Cart } from "../cart";
import {
  useMediaQuery,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";
import { db, auth } from "../../../firebase/config";
import HomeIcon from "@mui/icons-material/Home";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import { setCurrentRestaurant } from "../../../redux/publicReducer/actions";
import NoteIcon from "@mui/icons-material/Note";
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import { set } from "date-fns";

const enableKiosk = process.env.REACT_APP_ENABLE_EKIOSK;

// MUI v5 styled components following best practices
const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  "&:focus-within": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
  // Kiosk-specific styling
  ...(enableKiosk === "1" && {
    backgroundColor: alpha(theme.palette.common.white, 0.85),
    border: `1px solid ${alpha(theme.palette.common.black, 0.23)}`,
    "&:hover": {
      backgroundColor: alpha(theme.palette.common.white, 0.95),
    },
    "&:focus-within": {
      backgroundColor: alpha(theme.palette.common.white, 1),
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`,
    },
  }),
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color:
    enableKiosk === "0" ? "inherit" : alpha(theme.palette.common.black, 0.54),
  // Kiosk-specific positioning
  ...(enableKiosk === "1" && {
    transform: "scale(1.2)",
  }),
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    // Vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    minWidth: "300px",
    fontSize: enableKiosk === "0" ? "1rem" : "1.5rem",
    height: enableKiosk === "0" ? "20px" : "30px",
    [theme.breakpoints.up("sm")]: {
      width: enableKiosk === "0" ? "12ch" : "15ch",
      "&:focus": {
        width: enableKiosk === "0" ? "20ch" : "18ch",
      },
    },
    // Kiosk-specific styling
    ...(enableKiosk === "1" && {
      "&::placeholder": {
        color: alpha(theme.palette.common.black, 0.6),
        opacity: 1,
      },
    }),
  },
}));

export const LowerNav = () => {
  const dispatch = useDispatch();
  const { search } = useSelector((state) => state.public);
  const match = useRouteMatch();
  const history = useHistory();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [layoutSearch, setLayoutSearch] = useState("default");
  const searchInputRef = useRef(null);
  const keyboardRef = useRef(null);
  const [caretPosition, setCaretPosition] = useState(0);
  const [isSearchActive, setIsSearchActive] = useState(false); // New state to manage input interaction

  // Function to handle clicks outside the search input and keyboard
  const handleOutsideClick = (event) => {
    if (
      searchInputRef.current &&
      !searchInputRef.current.contains(event.target) &&
      keyboardRef.current &&
      !keyboardRef.current.contains(event.target)
    ) {
      setKeyboardVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const handleShift = () => {
    setLayoutSearch(layoutSearch === "default" ? "shift" : "default");
  };

  const handleOnKeyPress = (button) => {
    if (!isSearchActive) return; // Do nothing if search is not active yet

    let newSearchValue = search;
    let newCaretPosition = caretPosition;

    switch (button) {
      case "{shift}":
      case "{lock}":
        handleShift();
        break;
      case "{enter}":
        setKeyboardVisible(false);
        break;
      case "{bksp}":
        if (caretPosition > 0) {
          newSearchValue =
            search.slice(0, caretPosition - 1) + search.slice(caretPosition);
          newCaretPosition = caretPosition - 1; // Move caret back after deleting
        }
        break;
      case "{space}":
        newSearchValue =
          search.slice(0, caretPosition) + " " + search.slice(caretPosition);
        newCaretPosition = caretPosition + 1; // Move caret forward after adding space
        break;
      default:
        newSearchValue =
          search.slice(0, caretPosition) + button + search.slice(caretPosition);
        newCaretPosition = caretPosition + 1; // Move caret forward after adding character
    }

    // Update the caret position correctly after each input
    setCaretPosition(newCaretPosition);

    // Dispatch action to update search value
    dispatch({ type: "SET_SEARCH", payload: newSearchValue });
  };

  useEffect(() => {
    if (!isSearchActive) return; // Do nothing if search is not active yet

    // Focus the input element and set the caret position after state updates
    if (searchInputRef.current) {
      const inputElement = searchInputRef.current.querySelector("input");
      if (inputElement) {
        inputElement.focus();
        // Set caret position using selectionStart and selectionEnd
        inputElement.setSelectionRange(caretPosition, caretPosition);
      }
    }
  }, [search, caretPosition, isSearchActive]);

  const handleSearchChange = (e) => {
    if (!isSearchActive) return; // Do nothing if search is not active yet

    const inputValue = e.target.value;
    const inputElement = e.target;
    const newCaretPosition = inputElement.selectionStart;

    // Update search value and caret position from keyboard input
    dispatch({ type: "SET_SEARCH", payload: inputValue });
    setCaretPosition(newCaretPosition);
  };

  const handleCaretChange = (event) => {
    if (!isSearchActive) return; // Do nothing if search is not active yet
    setCaretPosition(event.target.selectionStart);
  };

  const keyboardLayout = {
    default: [
      "q w e r t y u i o p {bksp}",
      "a s d f g h j k l {enter}",
      "{shift} z x c v b n m , . {shift}",
      "{space}",
    ],
    shift: [
      "Q W E R T Y U I O P {bksp}",
      "A S D F G H J K L {enter}",
      "{shift} Z X C V B N M , . {shift}",
      "{space}",
    ],
  };

  const handleFocusSearch = () => {
    setIsSearchActive(true); // Activate search only when input is focused
    setKeyboardVisible(true);
    setTimeout(() => {
      searchInputRef.current.querySelector("input").focus();
    }, 0);
  };

  return (
    <Box
      sx={{
        width: "100%",
        position: "relative",
        bottom: "10px",
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Search>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
            }}
            sx={{
              width: "100%",
              backgroundColor: "#CCC",
              borderRadius: "4px",
              padding: "0.25rem",
            }}
          >
            <Box ref={searchInputRef}>
              <StyledInputBase
                placeholder="Search…"
                value={search}
                onChange={handleSearchChange} // Handle input changes from the keyboard
                onFocus={handleFocusSearch}
                onClick={handleCaretChange} // Update caret position on click
                onKeyUp={handleCaretChange} // Update caret position on keyup
                inputProps={{
                  "aria-label": "search",
                  sx: {
                    "&::placeholder": {
                      opacity: 1,
                      color:
                        enableKiosk === "0" ? "inherit" : "rgba(0, 0, 0, 0.6)",
                    },
                  },
                }}
              />
            </Box>
            {keyboardVisible && parseInt(enableKiosk) === 1 && (
              <Box
                ref={keyboardRef}
                className="SearchKeyboard"
                sx={{
                  width: "100%",
                  border: "none",
                  position: "fixed",
                  left: "0",
                  zIndex: 9999,
                  backgroundColor: "white",
                  boxShadow: 3,
                }}
              >
                <Keyboard
                  layout={keyboardLayout}
                  layoutName={layoutSearch}
                  onKeyPress={handleOnKeyPress}
                  inputName="search"
                />
              </Box>
            )}
          </Box>
        </Search>
      </Toolbar>
    </Box>
  );
};

export function NavBar() {
  const dispatch = useDispatch();
  const { search, user } = useSelector((state) => state.public);
  const match = useRouteMatch();
  const history = useHistory();
  const isMobile = useMediaQuery("(max-width:600px)");
  // const restaurantsRef = db.collection("admins").doc(match.params.restaurantId);
  const [dynamicPages, setDynamicPages] = useState([]);

  const [restaurantData, setRestaurantData] = useState({});

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  // useEffect(() => {
  //   const unsub = restaurantsRef.onSnapshot((doc) => {
  //     setRestaurantData({ ...doc.data(), id: doc.id });
  //   });

  //   return unsub;
  // }, []);

  // useEffect(() => {
  //   const unsub = db
  //     .collection("contentsManagement")
  //     .where("user", "==", match.params.restaurantId)
  //     .onSnapshot((snapshot) => {
  //       let _dynamicPages = [];
  //       snapshot.docs.forEach((doc) => {
  //         _dynamicPages.push({ ...doc.data(), id: doc.id });
  //       });
  //       setDynamicPages(_dynamicPages);
  //     });

  //   return unsub;
  // }, []);

  useEffect(() => {
    if (restaurantData) {
      dispatch(setCurrentRestaurant(restaurantData));
    } else {
      dispatch(setCurrentRestaurant(""));
    }
  }, [restaurantData]);

  return (
    <>
      <Toolbar
        elevation={0}
        sx={{
          backgroundColor: "transparent",
          position: "fixed",
          zIndex: 1100,
          right: "0",
          top: enableKiosk === "0" ? "0" : "10px",
          color: "black",
        }}
      >
        {/* <IconButton 
            edge="start"
            sx={{ mr: 2 }}
            color="inherit"
            aria-label="open drawer"
            onClick={handleClick}
          >
            <MenuIcon />
          </IconButton> */}

        {/* <Typography
            sx={{ 
              flexGrow: 1,
              width: "100%",
              display: { xs: 'none', sm: 'block' },
              cursor: "pointer",
              textAlign: "center"
            }}
            variant={isMobile ? "caption" : "h6"}
            noWrap
            onClick={() => {
              history.push(`/ menu / ${ restaurantData.id }`);
            }}
          >
           <>
          </Typography> */}

        {/*  <img
            src="/static/AJ_Sliders_Logo.svg"
            style={{ height: "2rem", width: "auto" }}
            sx={{ flexGrow: 1 }}
            onClick={() => {
              history.push("/");
            }}
          /> */}

        {isMobile && <Box sx={{ flexGrow: 1 }} />}

        <Box
          sx={{
            width: isMobile ? "100%" : "auto",
            display: "block",
            justifyContent: "flex-end",
          }}
        >
          <Cart isHidden={0} />
        </Box>
      </Toolbar>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box
          sx={{
            height: "100vh",
            maxHeight: "100vh",
            overflowY: "auto",
            position: "fixed",
            left: "0px",
            top: "0px",
            width: isMobile ? "80vw" : "30vw",
            backgroundColor: "bisque",
            padding: "0rem 2rem",
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              marginBottom: "1rem",
              marginLeft: "-30px",
            }}
          >
            <CloseIcon fontSize="large" sx={{ fontSize: "2.5rem" }} />
          </IconButton>

          <Box
            sx={{
              width: "100px",
              height: "100px",
              borderRadius: "50%",
            }}
          >
            <Box
              component="img"
              src="/static/logo.webp"
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: "10%",
              }}
            />
          </Box>

          <List sx={{ marginTop: "1rem" }}>
            <ListItem
              sx={{
                borderBottom: "1px solid gray",
                borderTop: "1px solid gray",
                width: "90%",
                borderCollapse: "collapse",
              }}
              button
              onClick={() => {
                history.push("/");
              }}
            >
              <ListItemIcon>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText primary="Home" />
            </ListItem>
            <ListItem
              sx={{
                borderBottom: "1px solid gray",
                borderTop: "1px solid gray",
                width: "90%",
                borderCollapse: "collapse",
              }}
              button
              onClick={() => {
                history.push("/menu");
              }}
            >
              <ListItemIcon>
                <RestaurantMenuIcon />
              </ListItemIcon>
              <ListItemText primary="Order Now" />
            </ListItem>
            <ListItem
              sx={{
                borderBottom: "1px solid gray",
                width: "90%",
                borderCollapse: "collapse",
              }}
              button
              onClick={() => history.push("/about")}
            >
              <ListItemIcon>
                <AutoStoriesIcon />
              </ListItemIcon>
              <ListItemText primary="About Us" />
            </ListItem>
            <ListItem
              sx={{
                borderBottom: "1px solid gray",
                width: "90%",
                borderCollapse: "collapse",
              }}
              button
              onClick={() => history.push("/team")}
            >
              <ListItemIcon>
                <PeopleIcon />
              </ListItemIcon>
              <ListItemText primary="Team" />
            </ListItem>
            <ListItem
              sx={{
                borderBottom: "1px solid gray",
                width: "90%",
                borderCollapse: "collapse",
              }}
              button
              onClick={() => history.push("/contact")}
            >
              <ListItemIcon>
                <PhoneIcon />
              </ListItemIcon>
              <ListItemText primary="Contact Us" />
            </ListItem>

            {dynamicPages.map((page) => {
              return (
                <ListItem
                  key={page.id}
                  sx={{
                    borderBottom: "1px solid gray",
                    width: "90%",
                    borderCollapse: "collapse",
                  }}
                  button
                  onClick={() => {
                    history.push(
                      `/ ${match.params.restaurantId} / dynamic / ${page.name} / ${page.id}`
                    );
                    handleClose();
                  }}
                >
                  <ListItemIcon>
                    <NoteIcon />
                  </ListItemIcon>
                  <ListItemText primary={page.name} />
                </ListItem>
              );
            })}
            {user && user?.userType === "CUSTOMER" && (
              <>
                <ListItem
                  button
                  onClick={() => {}}
                  sx={{
                    borderBottom: "1px solid gray",
                    width: "90%",
                  }}
                >
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText primary="Profile" />
                </ListItem>
                <ListItem
                  button
                  onClick={() => {
                    history.push("/order-history");
                  }}
                  sx={{
                    borderBottom: "1px solid gray",
                    width: "90%",
                  }}
                >
                  <ListItemIcon>
                    <FastfoodIcon />
                  </ListItemIcon>
                  <ListItemText primary="Your Orders" />
                </ListItem>

                <ListItem
                  button
                  onClick={() => {
                    auth.signOut().then(() => {
                      history.push("/menu/" + match.params.restaurantId);
                    });
                  }}
                  sx={{
                    borderBottom: "1px solid gray",
                    width: "90%",
                  }}
                >
                  <ListItemIcon>
                    <ExitToAppIcon />
                  </ListItemIcon>
                  <ListItemText primary="Sign out" />
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Popover>
    </>
  );
}
